import React, { Dispatch, SetStateAction } from 'react';

import { DegreeQuota, PaymentMethodType } from 'Jsx/CheckoutV4/types';
import { METHODS } from 'Jsx/CheckoutV4/common/constants';
import { isCardPaymentMethod } from 'Jsx/CheckoutV4/utils';
import { useCheckoutContext } from 'Jsx/CheckoutV4/Context';

import Stripe from './Stripe';
import Dlocal from './Dlocal';
import Rappi from './Rappi';
import Paypal from './Paypal';
import Efecty from './Colombia/Offline/Efecty';
import PSE from './Colombia/Offline/PSE';
import OfflinePeru from './Peru/Offline';
import Payu from './Peru/Online/Payu';
import ArgOffline from './Argentina/Offline';
import OfflineBrasil from './Brasil/Offline';
import MercadoPago from './MercadoPago';
import CreditCard from './CreditCard';
import Niubiz from './Niubiz';
import SPEI from './Mexico/Offline/SPEI';
import OXXO from './Mexico/Offline/OXXO';
import TestingPaymentMethod from './TestingPaymentMethod';

const body = {
  [METHODS.PAYPAL]: <Paypal />,
  [METHODS.RAPPI_COLOMBIA]: <Rappi />,
  [METHODS.RAPPI_MEXICO]: <Rappi />,
  [METHODS.PAGO_EFECTIVO]: <OfflinePeru />,
  [METHODS.EPAYCO_EFECTY]: <Efecty />,
  [METHODS.EPAYCO_PSE]: <PSE />,
  [METHODS.CONEKTA_SPEI]: <SPEI />,
  [METHODS.CONEKTA_OXXO]: <OXXO />,
  [METHODS.BRASIL_OFFLINE]: <OfflineBrasil />,
  [METHODS.RAPI_PAGO]: <ArgOffline />,
  [METHODS.PAGO_FACIL]: <ArgOffline />,
  [METHODS.MERCADOPAGO_ARGENTINA]: <MercadoPago />,
  [METHODS.MERCADOPAGO_MEXICO]: <MercadoPago />,
  [METHODS.NIUBIZ]: <Niubiz />,
  [METHODS.TESTING]: <TestingPaymentMethod />,
};

const PaymentMethod: React.FC<{
  symbol: string | null;
  quotas?: DegreeQuota[];
  selectedQuotaNumber: number;
  setSelectedQuotaNumber: Dispatch<SetStateAction<number>>;
  selectedPaymentMethod: PaymentMethodType;
  isDesktop?: boolean;
}> = ({
  symbol,
  quotas,
  selectedQuotaNumber,
  setSelectedQuotaNumber,
  selectedPaymentMethod,
  isDesktop,
}) => {
  const {
    checkoutProductDetail,
    flags: { useNewCreditCardFeature },
  } = useCheckoutContext();

  const isSelectedCardPaymentMethod = isCardPaymentMethod(
    selectedPaymentMethod?.title || '',
  );

  if (isSelectedCardPaymentMethod && useNewCreditCardFeature) {
    return (
      <CreditCard
        symbol={symbol}
        quotas={quotas}
        selectedQuotaNumber={selectedQuotaNumber}
        setSelectedQuotaNumber={setSelectedQuotaNumber}
      />
    );
  }

  const isDlocalPaymentMethod =
    selectedPaymentMethod?.title === METHODS.DLOCAL_MEXICO ||
    selectedPaymentMethod?.title === METHODS.DLOCAL_AR ||
    selectedPaymentMethod?.title === METHODS.DLOCAL_PERU ||
    selectedPaymentMethod?.title === METHODS.DLOCAL_COLOMBIA ||
    selectedPaymentMethod?.title === METHODS.DLOCAL_CHILE;

  if (!selectedPaymentMethod?.title) return null;

  if (isDlocalPaymentMethod) {
    return (
      <Dlocal
        itemType={checkoutProductDetail?.type}
        itemId={checkoutProductDetail?.originalId}
      />
    );
  }
  if (selectedPaymentMethod?.title === METHODS.PAYU_PERU) {
    return <Payu />;
  }

  if (isSelectedCardPaymentMethod) {
    return (
      <Stripe
        symbol={symbol}
        quotas={quotas}
        selectedQuotaNumber={selectedQuotaNumber}
        setSelectedQuotaNumber={setSelectedQuotaNumber}
        isDesktop={isDesktop}
        isStripeMexico={selectedPaymentMethod?.title === METHODS.STRIPE_MEXICO}
      />
    );
  }

  const otherPaymentMethods = body[selectedPaymentMethod?.title || ''];

  return otherPaymentMethods || null;
};

export default PaymentMethod;
